/**
 * Subscription success / confirmation page.
 * Stripe redirects here after the Checkout. We poll the backend until the
 * subscription has been synced (or surface a clean error after a few tries).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Loader2, CheckCircle2, ExternalLink, Calendar, CreditCard, AlertTriangle } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
};

const fmtMoney = (amount, currency) => {
  if (amount == null) return "—";
  const n = Number(amount) / 100;
  const sym = (currency || "gbp").toLowerCase() === "gbp" ? "£" : (currency || "").toUpperCase() + " ";
  return `${sym}${n.toFixed(2)}`;
};

export default function ChurchSubscribeSuccess() {
  const { user, loading: authLoading } = useAuth();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [church, setChurch] = useState(null);
  const [sub, setSub] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const nextPath = `/church-builder/${slug}/subscribe/success${sessionId ? `?session_id=${sessionId}` : ""}`;
      navigate(`/login?next=${encodeURIComponent(nextPath)}`);
      return;
    }

    let cancelled = false;
    const tick = async (count) => {
      if (cancelled) return;
      try {
        const { data: dash } = await api.get(`/church-builder/by-slug/${slug}/dashboard`);
        if (cancelled) return;
        setChurch(dash.church);
        // Trigger a sync against Stripe — this also updates Mongo.
        await api.post(`/church-builder/churches/${dash.church.id}/subscription/refresh`).catch(() => {});
        const { data: s } = await api.get(`/church-builder/churches/${dash.church.id}/subscription`);
        if (cancelled) return;
        setSub(s);
        if (s?.subscription_status && ["trialing", "active", "past_due"].includes(s.subscription_status)) {
          return; // done
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load subscription");
      }
      if (count < 8) {
        setAttempts(count + 1);
        setTimeout(() => tick(count + 1), 1500);
      }
    };
    tick(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user, authLoading, slug, sessionId]);

  const planName = useMemo(() => {
    const p = (sub?.subscription_plan || "").toString();
    return p ? p.charAt(0).toUpperCase() + p.slice(1) : "—";
  }, [sub]);

  const intervalLabel = sub?.subscription_interval === "annual" ? "/yr" : sub?.subscription_interval === "monthly" ? "/mo" : "";
  const statusLabel = (sub?.subscription_status || "pending").replace("_", " ");
  const pending = !sub?.subscription_status || sub.subscription_status === "incomplete";

  return (
    <div data-testid="cb-subscribe-success" className="max-w-2xl mx-auto px-6 py-16 text-white">
      {pending && !error && (
        <div className="rounded-xl border border-white/10 p-6 text-center">
          <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={28} />
          <div className="font-serif-display text-2xl mt-4">Confirming with Stripe…</div>
          <div className="text-xs text-neutral-400 mt-1">Attempt {attempts + 1}/8</div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-900/20 text-amber-200 p-6 flex items-start gap-3">
          <AlertTriangle size={18} className="mt-0.5" />
          <div>
            <div className="font-serif-display text-xl">We couldn't confirm your subscription</div>
            <div className="text-xs text-amber-100/80 mt-1">{error}</div>
            <Link to={`/church-builder/${slug}/dashboard`} className="inline-block mt-4 btn-gold px-5 py-2 rounded text-[10px] tracking-widest uppercase font-semibold">Go to dashboard</Link>
          </div>
        </div>
      )}

      {!pending && !error && sub && (
        <div data-testid="cb-subscribe-confirmed" className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-8">
          <CheckCircle2 size={28} className="text-[#D4AF37]" />
          <h1 className="font-serif-display text-3xl mt-3">You're all set</h1>
          <p className="text-sm text-neutral-300 mt-1">Your subscription is confirmed. Use your dashboard to publish your site.</p>

          <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
            <Row label="Church" value={church?.name || "—"} />
            <Row label="Plan" value={`${planName}`} testid="cs-plan" />
            <Row label="Status" value={statusLabel} testid="cs-status" />
            <Row label="Billing" value={`${fmtMoney(sub.subscription_amount, sub.subscription_currency)}${intervalLabel}`} testid="cs-billing" />
            <Row label="Trial ends" value={fmtDate(sub.trial_end_at)} icon={Calendar} testid="cs-trial-end" />
            <Row label="Next charge" value={fmtDate(sub.next_billing_at)} icon={CreditCard} testid="cs-next-billing" />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/church-builder/${slug}/dashboard`} data-testid="cs-go-dashboard" className="btn-gold px-5 py-2.5 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2">Open dashboard <ExternalLink size={12} /></Link>
            <a href={`/churches/${slug}`} target="_blank" rel="noreferrer" className="btn-ghost-gold px-5 py-2.5 rounded text-xs tracking-widest uppercase">Preview public site</a>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, icon: Icon, testid }) {
  return (
    <div className="border-b border-white/5 pb-2" data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-neutral-500 flex items-center gap-1">{Icon ? <Icon size={11} /> : null} {label}</div>
      <div className="text-white mt-1 capitalize">{value}</div>
    </div>
  );
}
